import { IRepossession } from "../../types/repossession.type";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";

const API_URL = `${import.meta.env.VITE_API_URL}/repossession`;

type LinkCollectorData = {
  cpf: string;
  pixKeyType: string;
  pixKey: string;
};

type ReportRepossession = {
  totalByCity: Record<string, { total: number }>;
  totalByStatus: Record<string, { total: number }>;
  totalByCollector: Record<string, { total: number }>;
  pendingPayments: Record<string, { plate: string; date: string }[]>;
};
interface GetRepossessionsParams {
  status?: string[] | string;
  search?: string;
  page?: number;
  itemsPerPage?: number;
  orderBy?: string;
  fields?: string;
}

const makeRequestDebounced = createDebounce(makeRequest, 200);
export const getRepossessions = async ({
  status = [],
  search = "",
  page = 1,
  itemsPerPage = 80,
  orderBy = "",
  fields = "startedAt,completedAt,status,reason,details,renter,collector,suspension,notes,notifications,createdAt,licensePlate,vehicleId,companyId,notesDetails",
}: GetRepossessionsParams = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });
  params.append("pageOptions[page]", String(page));
  params.append("pageOptions[itemsPerPage]", String(itemsPerPage));
  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);

  const url = `${API_URL}/?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IRepossession[]
  > & { totalItems: number; pageTotalItens: number };
  return {
    ok: result.ok,
    data: result.data,
    totalItems: result.totalItems,
    pageTotalItens: result.pageTotalItens,
  };
};

export const suspendRepossession = async (
  licensePlate: string,
  suspendedNotes: string
) => {
  const url = `${API_URL}/${licensePlate}/suspend`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(suspendedNotes),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRepossession>
  >;
};

export const linkCollector = async (
  licensePlate: string,
  data: LinkCollectorData
) => {
  const url = `${API_URL}/${licensePlate}/link-collector`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRepossession>
  >;
};

export async function getReportRepossession(companyId, startDate, endDate) {
  const params = new URLSearchParams({
    companyId,
    startDate,
    endDate,
  });
  const url = `${API_URL}/reports?${params.toString()}`;

  return makeRequestDebounced(url) as Promise<
    ApiResponseType<ReportRepossession | undefined>
  >;
}

export async function approvePaymentCollector(
  licensePlate: string,
  collector: string
) {
  const url = `${API_URL}/${licensePlate}/approve-payment-collector`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ collector }),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRepossession>
  >;
}

export async function cancelPaymentCollector(
  licensePlate: string,
  collector: string
) {
  const url = `${API_URL}/${licensePlate}/cancel-payment-collector`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ collector }),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRepossession>
  >;
}

export const findRepossessionByVehicleAndRenter = async (params: {
  vehicleId: string;
  renterId: string;
}): Promise<ApiResponseType<IRepossession | null>> => {
  const searchParams = new URLSearchParams({
    vehicleId: params.vehicleId,
    renterId: params.renterId,
  });

  const url = `${API_URL}/specific?${searchParams.toString()}`;

  const result = (await makeRequest(
    url
  )) as ApiResponseType<IRepossession | null>;

  return result;
};

export const addRepossessionNote = async (
  repossessionId: string,
  noteText: string
) => {
  const url = `${API_URL}/${repossessionId}/add-note`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ noteText }),
  };
  return makeRequest(url, options) as Promise<ApiResponseType<IRepossession>>;
};

export async function deleteCollector(
  repossessionId: string,
  collectorId: string
) {
  const url = `${API_URL}/${repossessionId}/collector/${collectorId}`;
  const options = {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  };

  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRepossession>
  >;
}
